import { convexHull } from './convexHull'
import type { Pt2 } from './mercator'

export const REVERSE_MARGIN_FACTOR = 0.2
export const REVERSE_MARGIN_MIN_M = 1000
export const REVERSE_MARGIN_MAX_M = 10000

/**
 * The app's no-hunting margin around a cluster: a fifth of its half-extent,
 * never under a kilometre (a single small parcel still gets a readable oval)
 * and never over ten (a county-sized ground does not paint the next county).
 */
export function reverseMargin(halfExtent: number): number {
  return Math.min(Math.max(halfExtent * REVERSE_MARGIN_FACTOR, REVERSE_MARGIN_MIN_M), REVERSE_MARGIN_MAX_M)
}

export interface RingCluster {
  /** Convex hull of every member ring's vertices. */
  hull: Pt2[]
  /** Indices into the `rings` argument of `clusterRings`. */
  ringIndices: number[]
}

interface Seed extends RingCluster {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

interface Extent {
  members: Seed[]
  minX: number
  minY: number
  maxX: number
  maxY: number
}

function seedOf(hull: Pt2[], ringIndices: number[]): Seed {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const [x, y] of hull) {
    if (x < minX) minX = x
    if (y < minY) minY = y
    if (x > maxX) maxX = x
    if (y > maxY) maxY = y
  }
  return { hull, ringIndices, minX, minY, maxX, maxY }
}

/**
 * Groups rings whose margin-expanded bounding boxes touch, transitively, the
 * way the app decides which grounds share one oval.
 *
 * Each round expands every cluster's box by `reverseMargin(half the larger
 * side)`, sweeps the boxes by min-x and merges the ones that overlap, then
 * rebuilds each merged cluster's hull; rounds repeat until one merges nothing.
 * The ellipse itself is fitted only once, per surviving cluster, by the caller:
 * fitting per ring and testing ellipses pairwise is what took the app 27 s on
 * a 1400-parcel ground before it moved to this sweep.
 */
export function clusterRings(rings: readonly Pt2[][]): RingCluster[] {
  let clusters: Seed[] = []
  rings.forEach((ring, index) => {
    const hull = convexHull(ring)
    if (hull.length === 0) return
    clusters.push(seedOf(hull, [index]))
  })

  while (clusters.length > 1) {
    const items = clusters
      .map((c) => {
        const m = reverseMargin(Math.max(c.maxX - c.minX, c.maxY - c.minY) / 2)
        return { c, minX: c.minX - m, minY: c.minY - m, maxX: c.maxX + m, maxY: c.maxY + m }
      })
      .sort((p, q) => p.minX - q.minX)

    const open: Extent[] = []
    const closed: Extent[] = []
    let merged = false
    for (const it of items) {
      for (let k = open.length - 1; k >= 0; k--) {
        if (open[k].maxX < it.minX) closed.push(...open.splice(k, 1))
      }
      let target: Extent | null = null
      for (let k = open.length - 1; k >= 0; k--) {
        const o = open[k]
        if (it.minY > o.maxY || it.maxY < o.minY) continue
        if (!target) {
          target = o
          o.members.push(it.c)
          o.minX = Math.min(o.minX, it.minX)
          o.minY = Math.min(o.minY, it.minY)
          o.maxX = Math.max(o.maxX, it.maxX)
          o.maxY = Math.max(o.maxY, it.maxY)
          merged = true
        } else {
          target.members.push(...o.members)
          target.minX = Math.min(target.minX, o.minX)
          target.minY = Math.min(target.minY, o.minY)
          target.maxX = Math.max(target.maxX, o.maxX)
          target.maxY = Math.max(target.maxY, o.maxY)
          open.splice(k, 1)
        }
      }
      if (!target) open.push({ members: [it.c], minX: it.minX, minY: it.minY, maxX: it.maxX, maxY: it.maxY })
    }
    closed.push(...open)
    if (!merged) break

    clusters = closed.map((e) => {
      if (e.members.length === 1) return e.members[0]
      return seedOf(
        convexHull(e.members.flatMap((m) => m.hull)),
        e.members.flatMap((m) => m.ringIndices),
      )
    })
  }

  return clusters.map(({ hull, ringIndices }) => ({ hull, ringIndices }))
}
